export const districtMode = {
  noDistrict: "NO_DISTRICT",
  optional: "DISTRICT_OPTIONAL",
  mandatory: "DISTRICT_MANDATORY",
};

export const getCustomerDistrictMode = (customer) => {
  if (!Array.isArray(customer?.districts) || customer.districts.length === 0) {
    return districtMode.noDistrict;
  }

  const mode = customer?.districtModeCode;

  if (
    mode === districtMode.noDistrict ||
    mode === districtMode.optional ||
    mode === districtMode.mandatory
  ) {
    return mode;
  }

  return districtMode.optional;
};

export const hasCustomerDistricts = (customer) =>
  getCustomerDistrictMode(customer) !== districtMode.noDistrict &&
  getCustomerDistrictOptions(customer).length > 0;

export const isCustomerDistrictRequired = (customer) =>
  getCustomerDistrictMode(customer) === districtMode.mandatory &&
  getCustomerDistrictOptions(customer).length > 0;

export const isCustomerDistrictEnabled = (customer) =>
  hasCustomerDistricts(customer);

export const getCustomerDistrictOptions = (customer) =>
  (getCustomerDistrictMode(customer) === districtMode.noDistrict ? [] : customer?.districts || [])
    .filter((district) => district?.id && district?.name)
    .map((district) => ({
      value: district.id,
      label: district.name,
    }));

export const getDistrictNameByGuid = (customer, districtGuid) => {
  if (!districtGuid || !hasCustomerDistricts(customer)) {
    return "";
  }

  const selectedDistrict = customer.districts.find(
    (district) => district?.id === districtGuid
  );

  return selectedDistrict?.name || "";
};

export const isDistrictGuidAllowed = (customer, districtGuid) => {
  if (!hasCustomerDistricts(customer)) {
    return true;
  }

  if (!districtGuid) {
    return !isCustomerDistrictRequired(customer);
  }

  return getCustomerDistrictOptions(customer).some(
    (option) => option.value === districtGuid
  );
};

export const sanitizePatientDistrictPayload = (customer, patient) => {
  const payload = {
    ...patient,
    residence: {
      ...patient?.residence,
    },
    domicile: {
      ...patient?.domicile,
    },
  };

  if (!hasCustomerDistricts(customer)) {
    delete payload.residence.districtGuid;
    delete payload.domicile.districtGuid;
  }

  return payload;
};

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const getPatientDistrictInfo = (patient) => ({
  residenceDistrictGuid: patient?.residence?.districtGuid,
  domicileDistrictGuid: patient?.domicile?.districtGuid,
});

const mergeDistrictInfo = (currentInfo, nextInfo) => ({
  residenceDistrictGuid: hasValue(currentInfo?.residenceDistrictGuid)
    ? currentInfo.residenceDistrictGuid
    : nextInfo?.residenceDistrictGuid,
  domicileDistrictGuid: hasValue(currentInfo?.domicileDistrictGuid)
    ? currentInfo.domicileDistrictGuid
    : nextInfo?.domicileDistrictGuid,
});

const applyDistrictInfoToPatient = (patient, districtInfo) => {
  if (!patient) {
    return patient;
  }

  const nextResidence = patient?.residence
    ? {
        ...patient.residence,
        ...(hasValue(districtInfo?.residenceDistrictGuid)
          ? { districtGuid: hasValue(patient?.residence?.districtGuid) ? patient.residence.districtGuid : districtInfo.residenceDistrictGuid }
          : {}),
      }
    : patient?.residence;

  const nextDomicile = patient?.domicile
    ? {
        ...patient.domicile,
        ...(hasValue(districtInfo?.domicileDistrictGuid)
          ? { districtGuid: hasValue(patient?.domicile?.districtGuid) ? patient.domicile.districtGuid : districtInfo.domicileDistrictGuid }
          : {}),
      }
    : patient?.domicile;

  return {
    ...patient,
    ...(nextResidence ? { residence: nextResidence } : {}),
    ...(nextDomicile ? { domicile: nextDomicile } : {}),
  };
};

export const normalizeOrderPatientsDistrictPayload = (payload) => {
  const normalizeSingleOrder = (order) => {
    if (!order || typeof order !== "object") {
      return order;
    }

    const orderDetailPatient = order?.orderDetail?.patient || order?.dettaglioOrdine?.patient;
    const cartPatients = (order?.productCart || [])
      .map((product) => product?.detail?.patient)
      .filter(Boolean);

    const districtInfo = [orderDetailPatient, ...cartPatients].reduce(
      (acc, patient) => mergeDistrictInfo(acc, getPatientDistrictInfo(patient)),
      { residenceDistrictGuid: undefined, domicileDistrictGuid: undefined }
    );

    const nextOrderDetail = order?.orderDetail
      ? {
          ...order.orderDetail,
          patient: applyDistrictInfoToPatient(order.orderDetail.patient, districtInfo),
        }
      : order?.orderDetail;

    const nextDettaglioOrdine = order?.dettaglioOrdine
      ? {
          ...order.dettaglioOrdine,
          patient: applyDistrictInfoToPatient(order.dettaglioOrdine.patient, districtInfo),
        }
      : order?.dettaglioOrdine;

    const nextProductCart = Array.isArray(order?.productCart)
      ? order.productCart.map((product) => ({
          ...product,
          detail: product?.detail
            ? {
                ...product.detail,
                patient: applyDistrictInfoToPatient(product.detail.patient, districtInfo),
              }
            : product?.detail,
        }))
      : order?.productCart;

    return {
      ...order,
      ...(nextOrderDetail ? { orderDetail: nextOrderDetail } : {}),
      ...(nextDettaglioOrdine ? { dettaglioOrdine: nextDettaglioOrdine } : {}),
      ...(nextProductCart ? { productCart: nextProductCart } : {}),
    };
  };

  if (Array.isArray(payload)) {
    return payload.map(normalizeSingleOrder);
  }

  return normalizeSingleOrder(payload);
};
